import {
  DECIMETER_TO_METER,
  FATHOM_TO_METER,
  FOOT_TO_METER,
  KM_TO_METER,
  KMH_TO_KNOTS,
  KMH_TO_MPH,
  METER_TO_FATHOM,
  METER_TO_FOOT,
  METER_TO_METER,
  MILE_TO_METER,
  NAUTICAL_MILE_TO_METER,
} from '../consts/units';
import { type DistanceUnit, Unit, UnitType } from '../types/units';

/**
 * Converts a numeric distance from a given unit into meters.
 *
 * @param value – the distance to convert
 * @param unitOfInputValue – the unit of the input value
 * @returns the equivalent distance in meters
 */
export function intoMeters(value: number, unitOfInputValue: DistanceUnit): number {
  switch (unitOfInputValue) {
    case Unit.Distance.dm:
      return value * DECIMETER_TO_METER;
    case Unit.Distance.m:
    case Unit.Distance.meter:
      return value * METER_TO_METER;
    case Unit.Distance.km:
    case Unit.Distance.kilometer:
      return value * KM_TO_METER;
    case Unit.Distance.mi:
    case Unit.Distance.mile:
      return value * MILE_TO_METER;
    case Unit.Distance.nmi:
    case Unit.Distance.nauticalMile:
      return value * NAUTICAL_MILE_TO_METER;
    case Unit.Distance.ft:
    case Unit.Distance.foot:
      return value * FOOT_TO_METER;
    case Unit.Distance.ftm:
    case Unit.Distance.fathom:
      return value * FATHOM_TO_METER;
    default:
      return value;
  }
}

/**
 * Returns the display label for the distance unit corresponding to the given unit system.
 *
 * @param unitType – the unit system (Metric, Imperial, or Nautical)
 * @param short – when `true` (default) returns the abbreviated form (e.g. `"km"`); when `false` returns the full name (e.g. `"kilometer"`)
 * @returns the unit label string, or `''` if the unit system is unrecognised
 */
export function distanceUnitByUnitType(unitType: UnitType, short = true): string {
  if (unitType === UnitType.IMPERIAL) {
    return short ? Unit.Distance.mi : Unit.Distance.mile;
  }
  if (unitType === UnitType.NAUTICAL) {
    return short ? Unit.Distance.nmi : Unit.Distance.nauticalMile;
  }
  if (unitType === UnitType.METRIC) {
    return short ? Unit.Distance.km : Unit.Distance.kilometer;
  }
  return '';
}

/**
 * Returns the display label for the velocity unit corresponding to the given unit system.
 *
 * @param unitType – the unit system (Metric, Imperial, or Nautical)
 * @returns the unit label string (e.g. `"km/h"`, `"mph"`, `"kn"`), or `''` if the unit system is unrecognised
 */
export function velocityUnitByUnitType(unitType: UnitType): string {
  if (unitType === UnitType.IMPERIAL) {
    return Unit.Velocity.mph;
  }
  if (unitType === UnitType.NAUTICAL) {
    return Unit.Velocity.kn;
  }
  if (unitType === UnitType.METRIC) {
    return Unit.Velocity.kmh;
  }
  return '';
}

/**
 * Returns the display label for the elevation unit corresponding to the given unit system.
 *
 * @param unitType – the unit system (Metric, Imperial, or Nautical)
 * @param short – when `true` (default) returns the abbreviated form (e.g. `"m"`); when `false` returns the full name (e.g. `"meter"`)
 * @returns the unit label string, or `''` if the unit system is unrecognised
 */
export function elevationUnitByUnitType(unitType: UnitType, short = true): string {
  if (unitType === UnitType.IMPERIAL) {
    return short ? Unit.Distance.ft : Unit.Distance.foot;
  }
  if (unitType === UnitType.NAUTICAL) {
    return short ? Unit.Distance.ftm : Unit.Distance.fathom;
  }
  if (unitType === UnitType.METRIC) {
    return short ? Unit.Distance.m : Unit.Distance.meter;
  }
  return '';
}

/**
 * Returns the display label for the pace unit corresponding to the given unit system.
 *
 * @param unitType – the unit system (Metric, Imperial, or Nautical)
 * @returns the unit label string (e.g. `"min/km"`, `"min/mi"`, `"min/nmi"`), or `''` if the unit system is unrecognised
 */
export function paceUnitByUnitType(unitType: UnitType): string {
  if (unitType === UnitType.IMPERIAL) {
    return Unit.Pace.minPerMi;
  }
  if (unitType === UnitType.NAUTICAL) {
    return Unit.Pace.minPerNmi;
  }
  if (unitType === UnitType.METRIC) {
    return Unit.Pace.minPerKm;
  }
  return '';
}

/**
 * Converts a speed value from km/h into the unit appropriate for the given unit system.
 *
 * @param speedInKMH – the speed in km/h, or `null`
 * @param unitType – the target unit system (Metric keeps km/h, Imperial converts to mph, Nautical to knots)
 * @returns the converted speed, or `-1` if `speedInKMH` is `null`
 */
export function unitAwareVelocity(speedInKMH: number | null, unitType: UnitType): number {
  if (speedInKMH == null) return -1;
  switch (unitType) {
    case UnitType.IMPERIAL:
      return speedInKMH * KMH_TO_MPH;
    case UnitType.NAUTICAL:
      return speedInKMH * KMH_TO_KNOTS;
    default:
      return speedInKMH;
  }
}

/**
 * Converts a speed value (m/s) into pace (min per distance unit) for the given unit system.
 *
 * Formula: pace = distanceInMeters / (60 * speedInMetersPerSecond)
 *
 * @param speedInMetersPerSecond – speed in m/s, or `null`
 * @param unitType – the target unit system (Metric → min/km, Imperial → min/mi, Nautical → min/nmi)
 * @returns the pace in the target unit, or `-1` if `speedInMetersPerSecond` is `null` or `0`
 */
export function unitAwarePace(speedInMetersPerSecond: number | null, unitType: UnitType): number {
  if (speedInMetersPerSecond == null) return -1;
  if (speedInMetersPerSecond === 0.0) return -1;

  const metersPerUnit =
    unitType === UnitType.IMPERIAL
      ? MILE_TO_METER
      : unitType === UnitType.NAUTICAL
        ? NAUTICAL_MILE_TO_METER
        : 1000;

  return metersPerUnit / (60 * speedInMetersPerSecond);
}

/**
 * Converts a distance value from meters into the elevation unit appropriate for the given unit system.
 *
 * Metric keeps meters, Imperial converts to feet, Nautical converts to fathoms.
 *
 * @param distanceInM – the distance in meters, or `null`/`undefined`
 * @param unitType – the target unit system
 * @returns the converted distance, or `-1` if `distanceInM` is `null` or `undefined`
 */
export function unitAwareDistance(
  distanceInM: number | null | undefined,
  unitType: UnitType,
): number {
  if (distanceInM == null) return -1;
  if (unitType === UnitType.IMPERIAL) {
    return distanceInM * METER_TO_FOOT;
  }
  if (unitType === UnitType.NAUTICAL) {
    return distanceInM * METER_TO_FATHOM;
  }
  return distanceInM;
}

/**
 * Inverse of {@link unitAwareVelocity}: converts a speed expressed in the given unit system's
 * velocity unit back into km/h. Metric keeps km/h, Imperial interprets the value as mph, Nautical
 * as knots.
 *
 * @param value – the speed in the unit system's velocity unit (km/h, mph, or knots)
 * @param unitType – the source unit system
 * @returns the equivalent speed in km/h
 */
export function unitAwareVelocityToKMH(value: number, unitType: UnitType): number {
  switch (unitType) {
    case UnitType.IMPERIAL:
      return value / KMH_TO_MPH;
    case UnitType.NAUTICAL:
      return value / KMH_TO_KNOTS;
    default:
      return value;
  }
}

/**
 * Inverse of {@link unitAwareDistance}: converts a distance expressed in the given unit system's
 * elevation-style unit back into meters. Metric keeps meters, Imperial interprets the value as
 * feet, Nautical as fathoms.
 *
 * @param value – the distance in the unit system's unit (meters, feet, or fathoms)
 * @param unitType – the source unit system
 * @returns the equivalent distance in meters
 */
export function unitAwareDistanceToMeters(value: number, unitType: UnitType): number {
  if (unitType === UnitType.IMPERIAL) {
    return value * FOOT_TO_METER;
  }
  if (unitType === UnitType.NAUTICAL) {
    return value * FATHOM_TO_METER;
  }
  return value;
}
